import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button, Form, Table, Offcanvas, Dropdown } from 'react-bootstrap';
import ReactPaginate from 'react-paginate';
import { useAuth } from '../../../context/AuthContext';
import withPermission from '../../../utils/withPermission';
import Swal from 'sweetalert2';
import moment from 'moment';
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

const backendUrl = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${backendUrl}/api/sales-targets`;

const SalesTargetAssignment = () => {
    const { user, permissions } = useAuth();
    const [targets, setTargets] = useState([]);
    const [salesUsers, setSalesUsers] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({
        user_id: '',
        target_amount: '',
        currency: user?.view_currency || 'AED',
        start_date: dayjs().startOf('month'),
        end_date: dayjs().endOf('month'),
        notes: '',
        status: 'Active'
    });
    const [isEdit, setIsEdit] = useState(false);
    const [selectedTarget, setSelectedTarget] = useState(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [pageCount, setPageCount] = useState(1);
    const [searchQuery, setSearchQuery] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState({});

    const WithDeletePermission = withPermission(({ children }) => children, ['salestarget-delete']);
    const WithEditPermission = withPermission(({ children }) => children, ['salestarget-edit']);
    const WithCreatePermission = withPermission(({ children }) => children, ['salestarget-create']);
    
    // Currency conversion
    const viewCurrency = user?.view_currency || 'AED';
    const usdToAed = parseFloat(user?.usd_to_aed || user?.exchange_rate || localStorage.getItem('exchange_rate') || 3.67);

    /**
     * Convert a stored amount from its original currency to the user's view currency.
     * @param {number} amount - The stored amount
     * @param {string} storedCurrency - The currency the amount was entered in ('AED' | 'USD')
     */
    const formatCurrency = (amount, storedCurrency = 'AED') => {
        const n = parseFloat(amount || 0);
        let displayAmount = n;

        if (storedCurrency === 'USD' && viewCurrency === 'AED') {
            displayAmount = n * usdToAed;        // USD stored → show in AED
        } else if (storedCurrency === 'AED' && viewCurrency === 'USD') {
            displayAmount = n / usdToAed;        // AED stored → show in USD
        }
        // Same currency → no conversion

        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: viewCurrency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(displayAmount);
    };

    /** Show the AED equivalent of a typed amount for user reference */
    const getAedEquivalent = (amount, currency) => {
        const n = parseFloat(amount || 0);
        if (!n) return null;
        if (currency === 'USD') return (n * usdToAed).toFixed(2);
        return n.toFixed(2);
    };

    /** Show the USD equivalent of a typed amount for user reference */
    const getUsdEquivalent = (amount, currency) => {
        const n = parseFloat(amount || 0);
        if (!n) return null;
        if (currency === 'AED') return (n / usdToAed).toFixed(2);
        return n.toFixed(2);
    };

    useEffect(() => {
        fetchTargets();
        fetchSalesUsers();
    }, []);

    const fetchTargets = async (page = 1) => {
        setIsLoading(true);
        try {
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.get(API_URL, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                params: {
                    page,
                    search: searchQuery
                }
            });

            if (response.data.status === 'success') {
                setTargets(response.data.data.data);
                setPageCount(response.data.data.last_page);
                setCurrentPage(response.data.data.current_page - 1);
            }
        } catch (error) {
            console.error('Error fetching sales targets:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchSalesUsers = async () => {
        try {
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.get(`${backendUrl}/api/users`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                params: {
                    department: 'Sales'
                }
            });

            const allUsers = response.data.users?.data || response.data.users || [];
            
            // Filter: Exclude current user and higher-level roles (Managers/Admins)
            const currentUser = JSON.parse(localStorage.getItem('user-info'))?.user;
            const filtered = allUsers.filter(u => {
                const isSelf = u.id === currentUser?.id;
                // Higher roles are those where parent_id is null
                const hasHigherRole = u.roles?.some(r => r.parent_id === null || r.parent_id === undefined);
                return !isSelf && !hasHigherRole;
            });

            setSalesUsers(filtered);
        } catch (error) {
            console.error('Error fetching sales users:', error);
        }
    };

    const handleAddTarget = () => {
        setFormData({
            user_id: '',
            target_amount: '',
            currency: user?.view_currency || 'AED',
            start_date: dayjs().startOf('month'),
            end_date: dayjs().endOf('month'),
            notes: '',
            status: 'Active'
        });
        setIsEdit(false);
        setSelectedTarget(null);
        setShowModal(true);
        setErrors({});
    };

    const handleEditTarget = (target) => {
        setFormData({
            user_id: target.user_id,
            target_amount: target.target_amount,
            // Restore the currency the target was originally entered in
            currency: target.currency || 'AED',
            start_date: dayjs(target.start_date),
            end_date: dayjs(target.end_date),
            notes: target.notes || '',
            status: target.status
        });
        setIsEdit(true);
        setSelectedTarget(target);
        setShowModal(true);
        setErrors({});
    };

    const handleDeleteTarget = async (id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                const userInfo = JSON.parse(localStorage.getItem('user-info'));
                const origin = localStorage.getItem('user-origin');
                const token = userInfo?.token;

                await axios.delete(`${API_URL}/${id}`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Tenant-Origin': origin,
                    }
                });

                setTargets(targets.filter(t => t.id !== id));
                Swal.fire({
                    icon: 'success',
                    title: 'Target has been deleted.',
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true,
                });
            } catch (error) {
                console.error('Error deleting target:', error);
                Swal.fire('Error!', 'Failed to delete target.', 'error');
            }
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.user_id) newErrors.user_id = 'User is required';
        if (!formData.target_amount) newErrors.target_amount = 'Target amount is required';
        if (!formData.start_date) newErrors.start_date = 'Start date is required';
        if (!formData.end_date) newErrors.end_date = 'End date is required';
        return newErrors;
    };

    const handleSubmit = async () => {
        const validationErrors = validateForm();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        try {
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const payload = {
                ...formData,
                target_amount: parseFloat(formData.target_amount),
                currency: formData.currency,          // store the currency with the record
                start_date: formData.start_date.format('YYYY-MM-DD'),
                end_date: formData.end_date.format('YYYY-MM-DD'),
                assigned_by: user.id
            };

            if (isEdit) {
                const response = await axios.put(`${API_URL}/${selectedTarget.id}`, payload, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Tenant-Origin': origin,
                    }
                });
                if (response.data.status === 'success') {
                    setTargets(targets.map(t => t.id === selectedTarget.id ? response.data.data : t));
                    Swal.fire({
                        icon: 'success',
                        title: 'Target has been updated.',
                        toast: true,
                        position: 'top-end',
                        showConfirmButton: false,
                        timer: 3000,
                        timerProgressBar: true,
                    });
                }
            } else {
                const response = await axios.post(API_URL, payload, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Tenant-Origin': origin,
                    }
                });
                if (response.data.status === 'success') {
                    setTargets([response.data.data, ...targets]);
                    Swal.fire({
                        icon: 'success',
                        title: 'Target has been assigned.',
                        toast: true,
                        position: 'top-end',
                        showConfirmButton: false,
                        timer: 3000,
                        timerProgressBar: true,
                    });
                }
            }
            setShowModal(false);
        } catch (error) {
            console.error('Error saving target:', error);
            Swal.fire('Error!', error.response?.data?.message || 'Failed to save target.', 'error');
        }
    };

    const handlePageChange = ({ selected }) => {
        fetchTargets(selected + 1);
    };

    return (
        <div className="main-content">
            <div className="page-content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-12">
                            <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                                <h4 className="mb-sm-0 font-size-18">Sales Target Assignment</h4>
                                <div className="page-title-right">
                                    <ol className="breadcrumb m-0">
                                        <li className="breadcrumb-item"><a href="javascript:void(0);">Sales</a></li>
                                        <li className="breadcrumb-item active">Targets</li>
                                    </ol>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-12">
                            <div className="card">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <div className="search-box">
                                            <div className="position-relative">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Search..."
                                                    value={searchQuery}
                                                    onChange={(e) => setSearchQuery(e.target.value)}
                                                    onKeyPress={(e) => e.key === 'Enter' && fetchTargets(1)}
                                                />
                                                <i className="bx bx-search-alt search-icon"></i>
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                            <WithCreatePermission>
                                                <button
                                                    type="button"
                                                    className="btn rounded-circle border d-flex align-items-center justify-content-center round-create-button"
                                                    onClick={handleAddTarget}
                                                    data-bs-toggle="tooltip"
                                                    data-bs-placement="top"
                                                    title="Assign Target"
                                                >
                                                    <i className="mdi mdi-plus"></i>
                                                </button>
                                            </WithCreatePermission>
                                        </div>
                                    </div>

                                    <div className="table-responsive">
                                        <table className="table align-middle table-nowrap table-check table-list">
                                            <thead className="table-sales-gradient">
                                                <tr>
                                                    <th style={{ width: '20px' }} className="align-middle">
                                                        <div className="form-check font-size-16">
                                                            <input className="form-check-input" type="checkbox" id="checkAll" />
                                                            <label className="form-check-label" htmlFor="checkAll"></label>
                                                        </div>
                                                    </th>
                                                    <th className="align-middle">#</th>
                                                    <th className="align-middle">Sales Person</th>
                                                    <th className="align-middle">Target Amount ({viewCurrency})</th>
                                                    <th className="align-middle">Period</th>
                                                    <th className="align-middle">Status</th>
                                                    <th className="align-middle">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {isLoading ? (
                                                    <tr>
                                                        <td colSpan="7" className="text-center">Loading...</td>
                                                    </tr>
                                                ) : targets.length > 0 ? (
                                                    targets.map((target, index) => (
                                                        <tr key={target.id}>
                                                            <td>
                                                                <div className="form-check font-size-16">
                                                                    <input className="form-check-input" type="checkbox" id={`targetCheck${target.id}`} />
                                                                    <label className="form-check-label" htmlFor={`targetCheck${target.id}`}></label>
                                                                </div>
                                                            </td>
                                                            <td>{currentPage * 15 + index + 1}</td>
                                                            <td>{target.user?.name}</td>
                                                            <td>
                                                                <strong>
                                                                    {/* Convert from stored currency to user's view currency */}
                                                                    {formatCurrency(target.target_amount, target.currency || 'AED')}
                                                                </strong>
                                                            </td>
                                                           
                                                            <td>
                                                                {moment(target.start_date).format('DD-MM-YYYY')} - {moment(target.end_date).format('DD-MM-YYYY')}
                                                            </td>
                                                            <td>
                                                                <span className={`badge bg-${target.status === 'Active' ? 'success' : 'danger'}`}>
                                                                    {target.status}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <div className="dropdown">
                                                                    <i
                                                                        className="mdi mdi-dots-horizontal font-size-18"
                                                                        data-bs-toggle="dropdown"
                                                                        aria-expanded="false"
                                                                        style={{ cursor: 'pointer' }}
                                                                    ></i>
                                                                    <ul className="dropdown-menu dropdown-menu-end font-size-14">
                                                                        <li>
                                                                            <WithEditPermission>
                                                                                <button className="dropdown-item" onClick={() => handleEditTarget(target)}>
                                                                                    Edit
                                                                                </button>
                                                                            </WithEditPermission>
                                                                        </li>
                                                                        <li>
                                                                            <WithDeletePermission>
                                                                                <button className="dropdown-item" onClick={() => handleDeleteTarget(target.id)}>
                                                                                    Delete
                                                                                </button>
                                                                            </WithDeletePermission>
                                                                        </li>
                                                                    </ul>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="8" className="text-center">No targets found</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    <div className="d-flex justify-content-end mt-3">
                                        <ReactPaginate
                                            previousLabel={"Previous"}
                                            nextLabel={"Next"}
                                            pageCount={pageCount}
                                            onPageChange={handlePageChange}
                                            containerClassName={"pagination pagination-rounded justify-content-end mb-2"}
                                            activeClassName={"active"}
                                            pageClassName={"page-item"}
                                            pageLinkClassName={"page-link"}
                                            previousClassName={"page-item"}
                                            previousLinkClassName={"page-link"}
                                            nextClassName={"page-item"}
                                            nextLinkClassName={"page-link"}
                                            forcePage={currentPage}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Offcanvas show={showModal} onHide={() => setShowModal(false)} placement='end' style={{ width: '450px' }}>
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title>{isEdit ? 'Edit Sales Target' : 'Assign Sales Target'}</Offcanvas.Title>
                </Offcanvas.Header>
                <hr style={{ margin: 0 }} />
                <Offcanvas.Body>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Form>
                            <Form.Group className="mb-3">
                                <Form.Label>Sales Person</Form.Label>
                                <Form.Select
                                    name="user_id"
                                    value={formData.user_id}
                                    onChange={handleInputChange}
                                    isInvalid={!!errors.user_id}
                                >
                                    <option value="">Select Sales Person</option>
                                    {salesUsers.map(u => (
                                        <option key={u.id} value={u.id}>{u.name}</option>
                                    ))}
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">{errors.user_id}</Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Target Amount</Form.Label>
                                <div className="input-group">
                                    {/* Currency selector — stored with the record */}
                                    <Form.Select
                                        name="currency"
                                        value={formData.currency}
                                        onChange={handleInputChange}
                                        style={{ maxWidth: '90px', flex: '0 0 90px' }}
                                        className="border-end-0"
                                    >
                                        <option value="AED">AED</option>
                                        <option value="USD">USD</option>
                                    </Form.Select>
                                    <Form.Control
                                        type="number"
                                        name="target_amount"
                                        value={formData.target_amount}
                                        onChange={handleInputChange}
                                        isInvalid={!!errors.target_amount}
                                        placeholder="0.00"
                                    />
                                    <Form.Control.Feedback type="invalid">{errors.target_amount}</Form.Control.Feedback>
                                </div>
                                {/* Live equivalent hint */}
                                {formData.target_amount > 0 && (
                                    <small className="text-muted mt-1 d-block">
                                        {formData.currency === 'AED'
                                            ? `≈ USD ${getUsdEquivalent(formData.target_amount, 'AED')}`
                                            : `≈ AED ${getAedEquivalent(formData.target_amount, 'USD')}`
                                        }
                                        {viewCurrency !== formData.currency && (
                                            <span className="ms-2 text-warning">
                                                <i className="bx bx-info-circle"></i> Your profile shows {viewCurrency} — amounts will be converted automatically
                                            </span>
                                        )}
                                    </small>
                                )}
                            </Form.Group>

                            <div className="row mb-3">
                                <div className="col-md-6">
                                    <Form.Label>Start Date</Form.Label>
                                    <DatePicker
                                        value={formData.start_date}
                                        onChange={(newValue) => setFormData({ ...formData, start_date: newValue })}
                                        format="DD-MM-YYYY"
                                        slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                    />
                                </div>
                                <div className="col-md-6">
                                    <Form.Label>End Date</Form.Label>
                                    <DatePicker
                                        value={formData.end_date}
                                        onChange={(newValue) => setFormData({ ...formData, end_date: newValue })}
                                        format="DD-MM-YYYY"
                                        slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                    />
                                </div>
                            </div>

                            <Form.Group className="mb-3">
                                <Form.Label>Status</Form.Label>
                                <Form.Select
                                    name="status"
                                    value={formData.status}
                                    onChange={handleInputChange}
                                >
                                    <option value="Active">Active</option>
                                    <option value="Inactive">Inactive</option>
                                </Form.Select>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Notes</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    name="notes"
                                    value={formData.notes}
                                    onChange={handleInputChange}
                                    placeholder="Optional notes..."
                                />
                            </Form.Group>
                        </Form>
                    </LocalizationProvider>
                </Offcanvas.Body>
                <div className="p-3 d-flex justify-content-end border-top">
                    <Button variant="secondary me-2" onClick={() => setShowModal(false)}>Close</Button>
                    <Button variant="primary" onClick={handleSubmit}>{isEdit ? 'Update' : 'Save'}</Button>
                </div>
            </Offcanvas>
        </div>
    );
};

export default withPermission(SalesTargetAssignment, ['salestarget-list']);
